import { type FormEvent, useState } from "react"
import { Link } from "react-router-dom"
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextArea,
  useLoad,
} from "../components/ui"
import { api } from "../lib/api"

type Workspace = {
  id: string
  name: string
  repoUrl: string | null
  gitBranch: string | null
  gitAuthType: string
  createdAt: string
  updatedAt: string
}

export function WorkspacesPage() {
  const { data, loading, error, reload } = useLoad(() =>
    api.get<{ workspaces: Workspace[] }>("/workspaces"),
  )
  const [showForm, setShowForm] = useState(false)

  return (
    <>
      <PageHeader
        title="Workspaces"
        subtitle="Configure development environments for your sessions."
        action={
          <Button onClick={() => setShowForm(true)} disabled={showForm}>
            Create Workspace
          </Button>
        }
      />

      <ErrorNote error={error} />

      {showForm && (
        <WorkspaceForm
          onDone={() => {
            setShowForm(false)
            reload()
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <Spinner />
      ) : data?.workspaces.length ? (
        <div className="flex flex-col gap-2">
          {data.workspaces.map((p) => (
            <ProjectRow key={p.id} workspace={p} onChanged={reload} />
          ))}
        </div>
      ) : (
        !showForm && (
          <Empty
            title="No workspaces yet"
            hint="Create a workspace to configure a development environment for your sessions."
          />
        )
      )}
    </>
  )
}

function ProjectRow({
  workspace,
  onChanged,
}: {
  workspace: Workspace
  onChanged: () => void
}) {
  const [deleting, setDeleting] = useState(false)
  const [editing, setEditing] = useState(false)

  const handleDelete = async () => {
    if (!confirm("Delete this workspace? All sessions will be deleted.")) return
    setDeleting(true)
    try {
      await api.delete(`/workspaces/${workspace.id}`)
      onChanged()
    } catch {
      setDeleting(false)
    }
  }

  if (editing) {
    return (
      <WorkspaceForm
        workspace={workspace}
        onDone={() => {
          setEditing(false)
          onChanged()
        }}
        onCancel={() => setEditing(false)}
      />
    )
  }

  return (
    <Card className="flex items-center justify-between gap-4 p-4">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-foreground">{workspace.name}</p>
        <p className="truncate text-sm text-muted">{workspace.repoUrl ?? "No repository"}</p>
        {workspace.gitBranch && (
          <p className="truncate text-xs text-faint">Branch: {workspace.gitBranch}</p>
        )}
        <p className="mt-1 text-xs text-faint">
          Auth: {workspace.gitAuthType === "none" ? "None" : workspace.gitAuthType.toUpperCase()}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Link to={`/workspaces/${workspace.id}/sessions`}>
          <Button variant="ghost">Sessions</Button>
        </Link>

        <Button variant="ghost" onClick={() => setEditing(true)}>
          Edit
        </Button>

        <Button variant="danger" onClick={handleDelete} disabled={deleting}>
          Delete
        </Button>
      </div>
    </Card>
  )
}

// An SSH key only authenticates `git@host:owner/repo.git`. Against an https URL
// git ignores it and prompts for a username instead — inside a container there
// is no one to answer, so the clone dies with git's exit 128 and the session
// times out waiting for a container that is already gone.
function authMismatch(repoUrl: string, gitAuthType: string): string | null {
  const url = repoUrl.trim()
  if (!url) return null
  if (gitAuthType === "ssh" && /^https?:\/\//i.test(url))
    return "SSH keys only work with an SSH URL. Use git@github.com:owner/repo.git, or switch authentication to HTTPS."
  if (gitAuthType === "https" && /^(git@|ssh:\/\/)/i.test(url))
    return "A token only works with an https:// URL. Use https://github.com/owner/repo.git, or switch authentication to SSH."
  return null
}

function WorkspaceForm({
  workspace,
  onDone,
  onCancel,
}: {
  workspace?: Workspace
  onDone: () => void
  onCancel: () => void
}) {
  const editing = workspace !== undefined
  const [name, setName] = useState(workspace?.name ?? "")
  const [mode, setMode] = useState<"empty" | "git">(workspace?.repoUrl ? "git" : "empty")
  const [repoUrl, setRepoUrl] = useState(workspace?.repoUrl ?? "")
  const [gitBranch, setGitBranch] = useState(workspace?.gitBranch ?? "")
  const [gitAuthType, setGitAuthType] = useState(workspace?.gitAuthType ?? "none")
  const [gitCredentials, setGitCredentials] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const credentialRequired = gitAuthType !== "none" && (!editing || gitAuthType !== workspace.gitAuthType)
  const mismatch = authMismatch(repoUrl, gitAuthType)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    const body = {
      name,
      repoUrl: mode === "git" ? repoUrl : undefined,
      gitBranch: mode === "git" ? gitBranch || undefined : undefined,
      gitAuthType: mode === "git" ? gitAuthType : undefined,
      gitCredentials: mode === "git" ? gitCredentials || undefined : undefined,
    }

    try {
      if (editing) await api.patch(`/workspaces/${workspace.id}`, body)
      else await api.post("/workspaces", body)
      onDone()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${editing ? "save" : "create"} workspace`,
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="p-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Field
          label="Workspace Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="My Workspace"
          required
        />
        <Select
          label="Workspace Type"
          value={mode}
          onChange={(e) => setMode(e.target.value as "empty" | "git")}
        >
          <option value="empty">Non-Repo Workspace</option>
          <option value="git">Git Workspace</option>
        </Select>
        {mode === "git" && (
          <>
            <Field
              label="Repository URL"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              placeholder="https://github.com/user/repo.git"
            />
            <Field
              label="Branch (optional)"
              value={gitBranch}
              onChange={(e) => setGitBranch(e.target.value)}
              placeholder="main"
            />
            <Select
              label="Git Authentication"
              value={gitAuthType}
              onChange={(e) => setGitAuthType(e.target.value)}
            >
              <option value="none">None (public repo)</option>
              <option value="https">HTTPS (Personal Access Token)</option>
              <option value="ssh">SSH (Deploy Key)</option>
            </Select>
            {mismatch && <p className="text-sm text-warning">{mismatch}</p>}
            {gitAuthType !== "none" && (
              <TextArea
                label={gitAuthType === "https" ? "Personal Access Token" : "SSH Deploy Key"}
                value={gitCredentials}
                onChange={(e) => setGitCredentials(e.target.value)}
                placeholder={
                  credentialRequired
                    ? gitAuthType === "https"
                      ? "ghp_xxxxxxxxxxxx"
                      : "-----BEGIN OPENSSH PRIVATE KEY-----\n..."
                    : "Leave blank to keep the current one"
                }
                required={credentialRequired}
              />
            )}
          </>
        )}

        <ErrorNote error={error} />

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting
              ? editing
                ? "Saving..."
                : "Creating..."
              : editing
                ? "Save Changes"
                : "Create Workspace"}
          </Button>
          <Button variant="ghost" type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  )
}
